import { useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import { Sidebar } from "./Sidebar";
import { Topbar } from "./Topbar";
import { useAuth } from "@/lib/auth";
import { OnboardingModal } from "@/components/onboarding/OnboardingModal";
import { VerifyEmailModal } from "@/components/auth/VerifyEmailModal";
import { AceiteDosDocumentosModal } from "@/components/auth/AceiteDosDocumentosModal";
import { PendingConnectInviteBanner } from "./PendingConnectInviteBanner";
import { PageHeaderProvider } from "@/lib/page-header";
import { ProductTourProvider } from "@/components/product-tour/ProductTourProvider";
import { TopoDoConteudo } from "./mobile/TituloDaPagina";
import { BarraDeAbas } from "./mobile/BarraDeAbas";
import { FaixaDeAtendimento } from "./mobile/FaixaDeAtendimento";
import { FaixaVerComo } from "./FaixaVerComo";
import { FaixaDeExclusao } from "./FaixaDeExclusao";
import { VoltarParaPedidos } from "./VoltarParaPedidos";
import { BotaoDeAjuda } from "@/components/ajuda/BotaoDeAjuda";
import { ProvedorDoRodape } from "@/components/ajuda/RodapeOcupado";
import { ROTAS_COM_VOLTAR_PROPRIO } from "./VoltarAoMenu";
import { cn } from "@/lib/cn";
import { ehEstacaoDeEtiquetas } from "@/lib/estacao-de-etiquetas";
import { useMontarProcedimentosPendentes } from "./use-montar-procedimentos-pendentes";

const LS_SIDEBAR_COLLAPSED = "sidebar_collapsed";

export function AppLayout() {
  const { user } = useAuth();
  // Persistido igual à sidebar da Agenda (`agenda_sidebarOpen`): quem recolhe o menu espera que
  // continue recolhido no próximo F5, não só enquanto o SPA não recarrega.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(LS_SIDEBAR_COLLAPSED) === "true";
  });

  const toggleCollapsed = () =>
    setCollapsed((v) => {
      const next = !v;
      if (typeof window !== "undefined") localStorage.setItem(LS_SIDEBAR_COLLAPSED, String(next));
      return next;
    });
  // Três gates, nessa ordem — e-mail primeiro: TeamInvite casa convite por e-mail digitado, não
  // por prova de posse da caixa, então ninguém age na plataforma antes de confirmar. Só depois
  // disso o onboarding entra (e pode nem aparecer, se a Membership já veio de um convite).
  // Depois, o aceite dos termos na versão vigente (no "ver como" o suporte só lê: não aceita por ninguém).
  const needsEmailVerification = !!user && !user.emailVerified;
  const needsAceite = !!user && user.emailVerified && user.aceiteDosDocumentosPendente && !user.verComo && !!user.versaoDosDocumentos;
  const needsOnboarding = !!user && user.emailVerified && !needsAceite && !user.onboardingCompleted;
  const { pathname } = useLocation();
  // Laboratório sem catálogo vai montar o catálogo antes de qualquer outra tela — quem pode mexer
  // no catálogo, claro; o resto da equipe usa o que houver.
  const org = user?.activeOrganization;
  const montarCatalogo =
    !!org && org.types.includes("DENTAL_LAB") && !org.catalogoMontadoEm && !!org.permissions["labCatalog.manage"] && !user?.verComo;
  // A clínica não tem passo de onboarding: os procedimentos das especialidades entram sozinhos
  // (e, nas que ficaram sem, na primeira entrada de quem gerencia).
  useMontarProcedimentosPendentes(
    !!org && org.types.includes("DENTAL_OFFICE") && !org.procedimentosMontadosEm && !!org.permissions["settings.manage"] && !user?.verComo,
  );
  const destino = montarCatalogo ? "/lab/comecar" : null;
  const blocked = needsEmailVerification || needsAceite || needsOnboarding;
  // Janela do atalho "DentalBook Etiquetas": PC de balcão que aceita e imprime. Só os pedidos, com a
  // barra superior; sem menu lateral. Só no laboratório: noutra organização a janela volta ao normal.
  const estacao = ehEstacaoDeEtiquetas() && !!org?.types.includes("DENTAL_LAB");
  const pagina = destino && pathname !== destino ? <Navigate to={destino} replace /> : estacao && pathname === "/inicio" ? <Navigate to="/lab/pedidos" replace /> : <Outlet />;

  return (
    <ProductTourProvider>
      <PageHeaderProvider>
        <ProvedorDoRodape>
        <div className="flex min-h-screen bg-background">
          {/* Menu lateral só no desktop. No celular a navegação é a barra de abas embaixo, e o resto
              das áreas fica na página Menu. */}
          {!estacao && <Sidebar collapsed={collapsed} onToggleCollapsed={toggleCollapsed} />}

          {/* overflow-x-clip (não overflow-x-hidden): "hidden" ainda conta como scroll container pra
              `position: sticky` (dá pra rolar via JS mesmo sem barra visível), quebrando qualquer sticky
              de uma página (ex: barra lateral de âncoras do Catálogo) mesmo a rolagem real acontecendo na
              janela. "clip" corta o overflow horizontal sem criar esse scroll container. */}
          <div className="flex min-h-screen min-w-0 flex-1 flex-col overflow-x-clip">
            {user?.verComo ? (
              // Faixa e barra presas juntas no topo: a barra já é sticky e cobriria uma faixa solta.
              <div className="sticky top-0 z-50">
                <FaixaVerComo nome={user.name ?? user.email} expiraEm={user.verComo.expiraEm} />
                <Topbar comLogo={estacao} />
              </div>
            ) : (
              <Topbar comLogo={estacao} />
            )}
            {!blocked && org?.exclusaoPrevistaPara && <FaixaDeExclusao prevista={org.exclusaoPrevistaPara} proprietario={org.role === "OWNER"} />}
            {!blocked && <PendingConnectInviteBanner />}
            {/* Folga embaixo: o fim da página não pode ficar atrás da barra de abas (celular) nem do
                "? Ajuda" fixo no canto. */}
            <main
              className={cn(
                "flex-1 p-4 max-md:pb-[calc(5.5rem+env(safe-area-inset-bottom))] sm:p-6 md:p-8",
                // A Agenda é tela cheia (margens negativas calculadas sobre o padding de sempre).
                !ROTAS_COM_VOLTAR_PROPRIO.includes(pathname) && "max-md:pb-[calc(8rem+env(safe-area-inset-bottom))] md:pb-20",
              )}
            >
              {!blocked && <TopoDoConteudo />}
              {!blocked && estacao && pathname !== "/lab/pedidos" && <VoltarParaPedidos />}
              {blocked ? null : pagina}
            </main>
          </div>

          {!blocked && <FaixaDeAtendimento />}
          {!blocked && !estacao && <BarraDeAbas />}
          {!blocked && !estacao && <BotaoDeAjuda />}

          {needsEmailVerification ? (
            <VerifyEmailModal />
          ) : needsAceite ? (
            <AceiteDosDocumentosModal versao={user.versaoDosDocumentos!} />
          ) : needsOnboarding ? (
            <OnboardingModal />
          ) : null}
        </div>
        </ProvedorDoRodape>
      </PageHeaderProvider>
    </ProductTourProvider>
  );
}
